// ── REBRAND 2025 — Warm Linen / Timeless Atmospheres ──
import React, { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { supabase } from '../shared/supabase';
import { useSessionLimit } from '../shared/hooks/useSessionLimit';
import ResumeSessionModal from '../Meditation/ResumeSessionModal';
import { PremiumUpgradeModal } from '../Components/PremiumSystem';

import { fetchActiveJourneys } from '../shared/api/journeys';
import { getUserStats } from '../shared/api/progress';
import { saveResumeData } from '../shared/utils/storage';
import Header from '../shared/components/Header';
import { buttonStyles, cardStyles } from '../shared/config/theme';
import { getJourneyConfig } from '../shared/constants/journeyConfig';

// ── Brand tokens as JS constants ──────────────────────────────
// These mirror tailwind.config.js so colours work immediately
// without waiting for Tailwind to rebuild output.css
const brand = {
  linen:         '#F7F4F0',   // page background
  linenCard:     '#FFFFFF',   // card surface
  linenBorder:   '#EDE7DC',   // subtle divider
  linenMid:      '#F4F0EA',   // secondary surface

  sand:          '#A8856A',   // dream-500 — primary accent
  sandDark:      '#8B6A50',   // dream-600
  sandLight:     '#F4EFE8',   // dream-100

  charcoal:      '#2B2420',   // heading text
  body:          '#52392B',   // body text (warm dark)
  secondary:     '#9C9080',   // secondary text (~60%)
  muted:         '#C4B9A8',   // placeholder

  // Heading font stack
  serif: "'Canela', 'Playfair Display', Georgia, serif",
  sans:  "'Inter', system-ui, sans-serif",
};
// ──────────────────────────────────────────────────────────────

const HomePage = ({
  onStartSession,
  onResumeSession,
  onNavigateToProgress,
  onNavigateToLibrary,
  onNavigateToMore,
  isAuthenticated,
  user,
  userProfile,
}) => {
  const [journeys, setJourneys]           = useState([]);
  const [streakCount, setStreakCount]     = useState(0);
  const [loading, setLoading]             = useState(true);
  const [activeSession, setActiveSession] = useState(null);
  const [showResumeModal, setShowResumeModal]   = useState(false);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const [showUpgradeModal, setShowUpgradeModal]   = useState(false);

  const {
    sessionCount,
    isPremium,
    canCreateSession,
    limitEnabled,
    sessionsRemaining,
    FREE_SESSION_LIMIT,
    refresh: refreshLimit,
  } = useSessionLimit(user?.id);

  // ── Journey card images ────────────────────────────────────
  const journeyImages = {
    'Confidence': `${process.env.PUBLIC_URL}/images/journeys/Confidence.png`,
    'Love':       `${process.env.PUBLIC_URL}/images/journeys/Love.png`,
    'Abundance':  `${process.env.PUBLIC_URL}/images/journeys/Abundance.png`,
    'Healing':    `${process.env.PUBLIC_URL}/images/journeys/Healing.png`,
    'Gratitude':  `${process.env.PUBLIC_URL}/images/journeys/Graditude.png`,
    'Focus':      `${process.env.PUBLIC_URL}/images/journeys/Focus.png`,
    'Clarity':    `${process.env.PUBLIC_URL}/images/journeys/Clarity.png`,
    'Joy':        `${process.env.PUBLIC_URL}/images/journeys/Joy.png`,
    'Courage':    `${process.env.PUBLIC_URL}/images/journeys/Courage.png`,
  };
  const defaultImage = `${process.env.PUBLIC_URL}/images/journeys/Abundance.png`;

  // ── Rotating quotes ───────────────────────────────────────
  const categoryQuotes = [
    { category: 'Confidence', emoji: '💪', quote: 'You are capable of amazing things. Trust yourself and move forward with strength.',     author: 'Your Inner Power' },
    { category: 'Love',       emoji: '❤️',  quote: 'Open your heart to give and receive freely. Love is the highest vibration.',           author: 'Universal Love'   },
    { category: 'Abundance',  emoji: '✨',  quote: 'Welcome prosperity in every form of your life. The universe is infinitely abundant.',  author: 'Infinite Wealth'  },
    { category: 'Healing',    emoji: '🌙',  quote: 'Release, renew, and allow yourself to restore. Healing begins with self-compassion.', author: 'Inner Peace'      },
    { category: 'Gratitude',  emoji: '🙏',  quote: 'Appreciate the present and attract more blessings. Gratitude transforms everything.', author: 'Thankful Heart'   },
    { category: 'Focus',      emoji: '🎯',  quote: 'Center your energy on what truly matters. Clarity comes from focused intention.',     author: 'Mindful Presence' },
    { category: 'Clarity',    emoji: '💎',  quote: 'See your path clearly and choose with calm intent. Wisdom guides your decisions.',    author: 'Clear Vision'     },
    { category: 'Joy',        emoji: '☀️',  quote: 'Let happiness flow through every moment. Joy is your natural state of being.',        author: 'Radiant Spirit'   },
    { category: 'Courage',    emoji: '🦁',  quote: 'Step boldly into the unknown with faith. Courage is taking action despite fear.',    author: 'Brave Soul'       },
  ];

  const currentQuote       = categoryQuotes[currentQuoteIndex];
  const currentQuoteConfig = getJourneyConfig(currentQuote.category);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuoteIndex((prev) => (prev + 1) % categoryQuotes.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadJourneys();
    if (isAuthenticated && user) loadUserProgress();
  }, [isAuthenticated, user]);

  const loadJourneys = async () => {
    try {
      const { journeys, error } = await fetchActiveJourneys();
      if (error) console.error('Error loading journeys:', error);
      else setJourneys(journeys);
    } finally {
      setLoading(false);
    }
  };

  const loadUserProgress = async () => {
    if (!user) return;
    try {
      const { stats, error } = await getUserStats(user.id);
      if (error) console.error('Error loading progress:', error);
      else if (stats) setStreakCount(stats.currentStreak || 0);
    } catch (error) {
      console.error('Error loading progress:', error);
    }
  };

  const handleStartSession = (journeyId) => {
    if (!canCreateSession && !isPremium) { setShowUpgradeModal(true); return; }
    onStartSession(journeyId);
    setTimeout(() => refreshLimit(), 1000);
  };

  const handleResumeSession = () => {
    if (activeSession) {
      saveResumeData(activeSession.id, activeSession.journey_id, activeSession.current_step);
      setShowResumeModal(false);
      onResumeSession(activeSession.journey_id);
    }
  };

  const handleDismissResume = () => { setShowResumeModal(false); setActiveSession(null); };

  // ── Loading state ─────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: brand.linen, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 40, height: 40, margin: '0 auto 12px',
            border: `2px solid ${brand.linenBorder}`,
            borderTopColor: brand.sand,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
          <p style={{ color: brand.secondary, fontSize: 13, fontFamily: brand.sans, letterSpacing: '0.04em' }}>
            Loading your journey…
          </p>
        </div>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', backgroundColor: brand.linen }}>

      {showResumeModal && activeSession && activeSession.journeys && (
        <ResumeSessionModal
          session={activeSession}
          journey={activeSession.journeys}
          onResume={handleResumeSession}
          onDismiss={handleDismissResume}
        />
      )}

      <PremiumUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="unlimited sessions"
        user={user}
      />

      <Header
        user={user}
        userProfile={userProfile}
        isPremium={isPremium}
        streakCount={streakCount}
        onMenuClick={onNavigateToMore}
        onProgressClick={onNavigateToProgress}
        showProgress={isAuthenticated}
        sessionsText={
          isPremium || !limitEnabled
            ? 'Unlimited sessions available'
            : `${sessionsRemaining} of ${FREE_SESSION_LIMIT} free sessions remaining`
        }
      />

      {/* ── Content ── */}
      <div style={{ padding: '32px 20px 40px', maxWidth: 680, margin: '0 auto' }}>

        {/* ── Hero ── */}
        <div style={{ marginBottom: 32 }}>

          {/* Eyebrow */}
          <p style={{
            fontFamily: brand.sans,
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: '0.10em',
            textTransform: 'uppercase',
            color: brand.secondary,
            marginBottom: 12,
          }}>
            Daily Practice
          </p>

          {/* Headline — Canela/Playfair at weight 400 */}
          <h2 style={{
            fontFamily: brand.serif,
            fontWeight: 400,
            fontSize: 'clamp(26px, 6vw, 36px)',
            color: brand.charcoal,
            lineHeight: 1.2,
            letterSpacing: '-0.015em',
            marginBottom: 14,
          }}>
            What energy do you want to
            <br />
            <span style={{ color: brand.sandDark, fontStyle: 'italic' }}>cultivate today?</span>
          </h2>

          {/* Body */}
          <p style={{
            fontFamily: brand.sans,
            fontWeight: 400,
            fontSize: 14,
            color: brand.secondary,
            lineHeight: 1.65,
          }}>
            Choose your intention and begin a personalised manifestation journey with{' '}
            <span style={{ color: brand.sand, fontWeight: 500 }}>AI coaching</span>,{' '}
            <span style={{ color: brand.sand, fontWeight: 500 }}>journalling</span>, and{' '}
            <span style={{ color: brand.sand, fontWeight: 500 }}>meditation</span>.
          </p>
        </div>

        {/* ── Journey Cards Grid ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 16,
          marginBottom: 24,
        }}>
          {journeys.map((journey) => (
            <JourneyCard
              key={journey.id}
              journey={journey}
              image={journeyImages[journey.title] || defaultImage}
              onStart={handleStartSession}
            />
          ))}
        </div>

        {/* ── Quote Card ── */}
        <div style={{
          background: brand.linenCard,
          borderRadius: 20,
          boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
          padding: 20,
          marginBottom: 20,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>

            {/* Icon */}
            <div style={{
              width: 44, height: 44, flexShrink: 0,
              background: `linear-gradient(135deg, ${brand.sandLight} 0%, ${brand.linenBorder} 100%)`,
              borderRadius: 12,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ fontSize: 20 }}>{currentQuote.emoji}</span>
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Category label */}
              <p style={{
                fontFamily: brand.sans,
                fontSize: 10,
                fontWeight: 500,
                letterSpacing: '0.10em',
                textTransform: 'uppercase',
                color: brand.sand,
                marginBottom: 8,
              }}>
                {currentQuote.category} Insight
              </p>

              {/* Quote text */}
              <p style={{
                fontFamily: brand.serif,
                fontWeight: 400,
                fontStyle: 'italic',
                fontSize: 13,
                color: brand.charcoal,
                lineHeight: 1.65,
                marginBottom: 8,
              }}>
                "{currentQuote.quote}"
              </p>

              {/* Author */}
              <p style={{
                fontFamily: brand.sans,
                fontSize: 11,
                fontWeight: 500,
                color: brand.sand,
              }}>
                — {currentQuote.author}
              </p>

              {/* Progress dots */}
              <div style={{ display: 'flex', gap: 6, marginTop: 14 }}>
                {categoryQuotes.map((_, idx) => (
                  <div
                    key={idx}
                    style={{
                      height: 4,
                      borderRadius: 999,
                      transition: 'all 0.5s ease',
                      width: idx === currentQuoteIndex ? 28 : 6,
                      background: idx === currentQuoteIndex ? brand.sand : brand.linenBorder,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Session limit banner ── */}
        {!isPremium && limitEnabled && (
          <div style={{
            background: brand.linenCard,
            border: `1px solid ${brand.linenBorder}`,
            borderRadius: 16,
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}>
            <div>
              <p style={{ fontFamily: brand.sans, fontSize: 13, fontWeight: 500, color: brand.charcoal, marginBottom: 2 }}>
                {sessionCount}/{FREE_SESSION_LIMIT} free sessions today
              </p>
              <p style={{ fontFamily: brand.sans, fontSize: 11, color: brand.secondary }}>
                Upgrade for unlimited access
              </p>
            </div>
            <button
              onClick={() => setShowUpgradeModal(true)}
              style={{
                background: `linear-gradient(135deg, ${brand.sand} 0%, ${brand.sandDark} 100%)`,
                color: '#FFFFFF',
                border: 'none',
                borderRadius: 999,
                padding: '8px 18px',
                fontSize: 12,
                fontWeight: 500,
                fontFamily: brand.sans,
                letterSpacing: '0.015em',
                cursor: 'pointer',
                flexShrink: 0,
                minHeight: 36,
              }}
            >
              Upgrade
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

// ── Journey Card — extracted for clarity ──────────────────────
const JourneyCard = ({ journey, image, onStart }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={() => onStart(journey.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        height: 'clamp(180px, 38vw, 280px)',  /* fluid: compact mobile → tall desktop */
        borderRadius: 20,
        overflow: 'hidden',
        cursor: 'pointer',
        boxShadow: hovered
          ? '0 16px 48px rgba(0,0,0,0.10)'
          : '0 8px 30px rgba(0,0,0,0.06)',
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'box-shadow 0.3s ease, transform 0.3s ease',
        backgroundImage: `url(${image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
      }}
    >
      {/* Gradient overlays */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.12) 55%, transparent 100%)',
      }} />
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(135deg, rgba(168,133,106,0.07) 0%, transparent 60%)',
      }} />

      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        padding: 14,
      }}>
        {/* Category pill */}
        <div style={{
          alignSelf: 'flex-start',
          background: 'rgba(0,0,0,0.28)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.18)',
          borderRadius: 999,
          padding: '4px 10px',
        }}>
          <span style={{
            color: 'rgba(255,255,255,0.92)',
            fontSize: 9,
            fontWeight: 500,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            fontFamily: "'Inter', sans-serif",
          }}>
            {journey.title}
          </span>
        </div>

        {/* Bottom content */}
        <div>
          <p style={{
            color: 'rgba(255,255,255,0.75)',
            fontSize: 11,
            lineHeight: 1.5,
            marginBottom: 10,
            fontFamily: "'Inter', sans-serif",
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {journey.description}
          </p>

          {/* Begin button */}
          <button
            style={{
              width: '100%',
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.32)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              borderRadius: 999,
              padding: '9px 14px',
              color: '#FFFFFF',
              fontSize: 12,
              fontWeight: 500,
              fontFamily: "'Inter', sans-serif",
              letterSpacing: '0.015em',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              cursor: 'pointer',
            }}
          >
            <Sparkles style={{ width: 12, height: 12 }} />
            Begin
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomePage;